// Node Module Imports
import React from 'react'

import { Link } from 'gatsby'
import PropTypes from 'prop-types'
import { FaExternalLinkAlt } from 'react-icons/fa'
import styled from 'styled-components'

// Component Imports
import { above, colors } from '../../style/design-system'
import { formatPhoneNumber } from '../../utils/phoneNumberFormatter'
import { addresses } from '../../utils/static-data/addresses'
import { footerLinks } from '../../utils/static-data/footerLinks'
import { phoneNumbers } from '../../utils/static-data/phoneNumbers'
import { UnderlinedAGa } from '../elements/UnderlinedAGoogleAnalytics'
import { UnderlinedLink } from '../elements/UnderlinedLink'
import SocialLinks from '../reusables/SocialLinks'

// Style Imports

// Utility Imports

// Get the Year
const today = new Date()
const year = today.getFullYear()

const GlobalFooter = ({ className }) => {
  const renderLinks = (arr) =>
    arr.map((item) => {
      const rand = Math.random()
      return item.notGatsbyLink === true ? (
        <a className="footer-link" href={item.href} key={rand} {...item.attributes}>
          {item.text}{' '}
          {item.external && (
            <>
              <FaExternalLinkAlt className="icon-sm" />
              <span className="sr-only">opens a new tab</span>
            </>
          )}
        </a>
      ) : (
        <Link className="footer-link" to={item.href} key={rand} {...item.attributes}>
          {item.text}
        </Link>
      )
    })

  const renderLocations = (obj) => {
    const locationKeys = Object.keys(obj)
    return locationKeys.map((location, idx) => {
      const currentLocation = locationKeys[idx]
      const associatedPhoneNumber = phoneNumbers[currentLocation]
      return addresses[location].includeInFooter ? (
        <div className="location" key={addresses[location].name}>
          <div>
            <strong>{addresses[location].name}</strong>
          </div>
          <a
            className="address-link"
            href={addresses[location].googleMapsLink}
            rel="noopnener noreferrer"
            target="_blank">
            <div>{addresses[location].addressLineOne}</div>
            <div>{addresses[location].addressLineTwo}</div>
            <span className="sr-only">opens a new tab</span>
          </a>
          <UnderlinedAGa
            color={colors.umaDarkGray}
            href={`tel:${associatedPhoneNumber}`}
            gaLabel="null"
            aria-label="Call Ultimate Medical Academy">
            {formatPhoneNumber(associatedPhoneNumber)}
          </UnderlinedAGa>
        </div>
      ) : null
    })
  }

  return (
    <footer className={className}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="container-fluid">
              <div className="be-ix-link-block">{/* Link Block Target Div */}</div>
            </div>
          </div>
          <div className="col-12 col-md-5 col-lg-4 resources">
            <div className="container-fluid">
              <h4>Resources</h4>
              <div className="row footer-links">
                <div className="col-6">{renderLinks(footerLinks.leftSide)}</div>
                <div className="col-6">{renderLinks(footerLinks.rightSide)}</div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-7 col-lg-8 follow-and-locations">
            <h4>Follow Us</h4>
            <SocialLinks showHoverColor />
            <div className="locations">
              <h4>Locations</h4>
              <div className="locations-list">{renderLocations(addresses)}</div>
              <div className="copyright">
                © {year} UMA Education, Inc. d/b/a Ultimate Medical Academy |{' '}
                <UnderlinedLink color={colors.umaDarkGray} to="/about/citations/">
                  Marketing&nbsp;Statistics
                </UnderlinedLink>{' '}
                |{' '}
                <UnderlinedLink color={colors.umaDarkGray} to="/about/privacy-policy/">
                  Privacy&nbsp;Policy
                </UnderlinedLink>
              </div>
              <div className="legal">
                <small>Ultimate Medical Academy does not recruit or enroll residents of the European Union.</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default styled(GlobalFooter)`
  padding: 1.25rem 0 10rem 0;
  background-color: ${colors.umaUltimateLightGray};
  ${above.lg`
    padding-bottom: 1.25rem;
  `}
  h4 {
    font-size: 1.25rem;
  }
  .icon-sm {
    width: 0.865rem;
    margin-top: -7px;
  }
  .resources {
    ${above.md`
      padding-left: 0;
      border-bottom: none;
    `};
    .footer-links {
      border-bottom: 0.0625rem solid #000;
      padding-bottom: 1.25rem;
      ${above.md`
        border-right: .0625rem solid #000;
        border-bottom: none;
        padding-bottom: 0;
      `}
      ${above.xl`
        padding-bottom: 0;
      `}
      .footer-link {
        display: block;
        margin-bottom: 0.5rem;
        line-height: 1.2;
        color: ${colors.umaDarkGray};
        ${above.md`
          font-size: 1rem;
        `}
        ${above.xl`
          font-size: 1.2rem;
        `}
      }
    }
  }
  .follow-and-locations {
    ${above.md`
      padding-left: 2.5rem;
    `}
    .locations {
      border-top: 0.0625rem solid #000;
      margin-top: 1.875rem;
      .locations-list {
        display: flex;
        justify-content: flex-start;
        flex-wrap: wrap;
      }
      .location {
        flex: 1 1 12.5rem;
        display: inline-block;
        margin-bottom: 1.25rem;
        padding-right: 0.625rem;
        .address-link {
          color: ${colors.umaDarkGray};
        }
      }
      .copyright {
        margin-top: 1.25rem;
        color: ${colors.umaDarkGray};
      }
    }
  }
`

GlobalFooter.propTypes = {
  className: PropTypes.string,
}

GlobalFooter.defaultProps = {
  className: '',
}

// TODO: Add proper gaLabel props for UnderlinedAGa components
// TODO: Change the key prop in the return of renderLinks to something other than Math.random()?
